import { BackupRounded, CloseOutlined, InsertDriveFile } from '@mui/icons-material'
import { Avatar, Box, IconButton, LinearProgress, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material'
import Dropzone, { ILayoutProps, IPreviewProps } from 'react-dropzone-uploader'
import { useTranslation } from 'react-i18next'
import 'react-dropzone-uploader/dist/styles.css'

const Layout = ({ input, previews, submitButton, dropzoneProps, files, extra: { maxFiles } }: ILayoutProps) => {
  return (
    <div>
      {previews}
      <div {...dropzoneProps}>{files.length < maxFiles && input}</div>
      {files.length > 0 && submitButton}
    </div>
  )
}

const Preview = ({ meta }: IPreviewProps) => {
  const { name, percent, previewUrl } = meta

  return (
    <Box sx={{ width: '100%', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
      <Box
        component="img"
        src={previewUrl}
        alt={name}
        sx={{
          width: '100%',
          height: '120px',
          objectFit: 'contain',
          borderRadius: 1,
          mb: 1
        }}
      />
      <Box>
        <Typography variant="caption" noWrap sx={{ display: 'block' }}>
          {name}
        </Typography>
        <LinearProgress variant="determinate" value={Math.round(percent)} />
      </Box>
    </Box>
  )
}

export function RemoveContent({ files, onHandleClick, darkMode = false }: { files: string[]; onHandleClick: (index: number) => void; darkMode?: boolean }) {
  const fileUrl = files[files.length - 1]
  if (!fileUrl) return null

  const fileName = decodeURIComponent(fileUrl.substring(fileUrl.lastIndexOf('/') + 1))

  return (
    <ListItem
      sx={{
        border: '1px solid',
        borderColor: darkMode ? 'grey.800' : 'divider',
        backgroundColor: darkMode ? '#171A1C' : 'transparent',
        borderRadius: 2,
        boxShadow: "none"
      }}
      secondaryAction={
        <IconButton edge="end" aria-label="Remove File" onClick={() => onHandleClick(0)} sx={{ color: darkMode ? 'common.white' : 'inherit' }}>
          <CloseOutlined />
        </IconButton>
      }
    >
      <ListItemAvatar>
        <Box
          component="img"
          src={fileUrl}
          alt={fileName}
          sx={{
            width: 80,
            height: 80,
            objectFit: 'contain',
            borderRadius: 1
          }}
        />
      </ListItemAvatar>
      <ListItemText
        primary={fileName}
        primaryTypographyProps={{
          noWrap: true,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          fontWeight: 'medium',
          variant: 'body2',
          marginLeft: 2,
          color: darkMode ? 'common.white' : 'text.primary'
        }}
      />
    </ListItem>
  )
}

function DropzoneForm({ name, handleInputChange, dir = 'resources' }: any) {
  const { t } = useTranslation(['global'])

  const getUploadParams = ({ meta }: any) => {
    return { url: `${import.meta.env.VITE_API_URL}/${dir}/upload/${meta.name}` }
  }

  const handleChangeStatus = ({ meta, file }: any, status: string) => {
    if (status === 'done') {
      handleInputChange(name, meta.name)
    } else if (status === 'removed') {
      handleInputChange(name, null)
    }
  }

  return (
    <Dropzone
      getUploadParams={getUploadParams}
      onChangeStatus={({ meta, file }, status) => handleChangeStatus({ meta, file }, status)}
      LayoutComponent={Layout}
      maxFiles={1}
      multiple={false}
      classNames={{ dropzone: 'dzu-dropzone-custom', inputLabel: 'dzu-inputlabel-custom' }}
      inputContent={
        <Box display="flex" flexDirection="column" alignItems="center">
          <BackupRounded />
          {t('dropzone-text')}
        </Box>
      }
      PreviewComponent={Preview}
    />
  )
}

export default DropzoneForm
